/**
 * Backoffice DI Registry
 *
 * App-specific dependency registration.
 * This file should be imported FIRST in app.ts to initialize
 * the DI container before any components load.
 */

import { DiContainer } from "ui-common/di";

import { CounterState } from "ui-common/services/counter/counter-state";
import { CounterService } from "ui-common/services/counter/counter-service";
import { SseState } from "ui-common/services/sse/sse-state";
import { SseService } from "ui-common/services/sse/sse-service";
import { RpcService } from "ui-common/services/rpc/rpc-service";
import { LiveChatState } from "ui-common/services/live-chat/chat-state";
import { LiveChatService } from "ui-common/services/live-chat/chat-service";
import { AuthState } from "ui-common/services/auth/auth-state";
import { AuthService } from "ui-common/services/auth/auth-service";
import { AuthProvider } from "ui-common/services/auth/auth-provider";
import type { ISseState } from "ui-common/services/sse/types";
import type { ILiveChatState } from "ui-common/services/live-chat/types";
import type { IAuthState } from "ui-common/services/auth/types";
import { NotificationTranslationService } from "ui-common/services/notification-translation/notification-translation-service";
import { CreatorLiveEventsState } from "../services/creator-live-events/creator-live-events-state";
import { CreatorLiveEventsService } from "../services/creator-live-events/creator-live-events-service";
import type { ICreatorLiveEventsState } from "../services/creator-live-events/types";
import {
  LiveEventFormState,
  asReadonlyLiveEventFormState,
} from "ui-common/services/live-event-form/live-event-form-state";
import { LiveEventFormService } from "ui-common/services/live-event-form/live-event-form-service";
import { BroadcasterDiagnosticsState } from "ui-common/services/broadcaster-diagnostics/broadcaster-diagnostics-state";
import { BroadcasterDiagnosticsService } from "ui-common/services/broadcaster-diagnostics/broadcaster-diagnostics-service";
import type { IBroadcasterDiagnosticsState } from "ui-common/services/broadcaster-diagnostics/types";

/**
 * Type-safe key constants.
 * Use these when registering and retrieving dependencies.
 */
export const KEYS = {
  counterState: "counterState",
  counterService: "counterService",
  sseState: "sseState",
  writableSseState: "writableSseState",
  sseService: "sseService",
  rpcService: "rpcService",
  liveChatState: "liveChatState",
  writableLiveChatState: "writableLiveChatState",
  liveChatService: "liveChatService",
  notificationTranslationService: "notificationTranslationService",
  authState: "authState",
  writableAuthState: "writableAuthState",
  authService: "authService",
  authProvider: "authProvider",
  creatorLiveEventsState: "creatorLiveEventsState",
  writableCreatorLiveEventsState: "writableCreatorLiveEventsState",
  creatorLiveEventsService: "creatorLiveEventsService",
  liveEventFormState: "liveEventFormState",
  writableLiveEventFormState: "writableLiveEventFormState",
  liveEventFormService: "liveEventFormService",
  broadcasterDiagnosticsState: "broadcasterDiagnosticsState",
  writableBroadcasterDiagnosticsState: "writableBroadcasterDiagnosticsState",
  broadcasterDiagnosticsService: "broadcasterDiagnosticsService",
} as const;

/**
 * Initialize the DI container with all dependencies.
 * Call this FIRST in app.ts before any other imports that use injection.
 */
export function initializeDi(): void {
  // Create instances
  const counterState = new CounterState();
  const counterService = new CounterService();
  const sseState = new SseState();
  const sseService = new SseService();
  const rpcService = new RpcService();
  const liveChatState = new LiveChatState();
  const liveChatService = new LiveChatService();
  const notificationTranslationService = new NotificationTranslationService();
  const authState = new AuthState();
  const authService = new AuthService();
  const authProvider = new AuthProvider();
  const creatorLiveEventsState = new CreatorLiveEventsState();
  const creatorLiveEventsService = new CreatorLiveEventsService();
  const liveEventFormState = new LiveEventFormState();
  const liveEventFormService = new LiveEventFormService();
  const broadcasterDiagnosticsState = new BroadcasterDiagnosticsState();
  const broadcasterDiagnosticsService = new BroadcasterDiagnosticsService();

  // Initialize container with all dependencies
  DiContainer.initialize({
    [KEYS.counterState]: counterState,
    [KEYS.counterService]: counterService,
    [KEYS.sseState]: sseState as ISseState,
    [KEYS.writableSseState]: sseState,
    [KEYS.sseService]: sseService,
    [KEYS.rpcService]: rpcService,
    [KEYS.liveChatState]: liveChatState as ILiveChatState,
    [KEYS.writableLiveChatState]: liveChatState,
    [KEYS.liveChatService]: liveChatService,
    [KEYS.notificationTranslationService]: notificationTranslationService,
    [KEYS.authState]: authState as IAuthState,
    [KEYS.writableAuthState]: authState,
    [KEYS.authService]: authService,
    [KEYS.authProvider]: authProvider,
    [KEYS.creatorLiveEventsState]: creatorLiveEventsState as ICreatorLiveEventsState,
    [KEYS.writableCreatorLiveEventsState]: creatorLiveEventsState,
    [KEYS.creatorLiveEventsService]: creatorLiveEventsService,
    [KEYS.liveEventFormState]: asReadonlyLiveEventFormState(liveEventFormState),
    [KEYS.writableLiveEventFormState]: liveEventFormState,
    [KEYS.liveEventFormService]: liveEventFormService,
    [KEYS.broadcasterDiagnosticsState]: broadcasterDiagnosticsState as IBroadcasterDiagnosticsState,
    [KEYS.writableBroadcasterDiagnosticsState]: broadcasterDiagnosticsState,
    [KEYS.broadcasterDiagnosticsService]: broadcasterDiagnosticsService,
  });

  // Initialize services that need it
  counterService.initialize();
  sseService.initialize();
  authService.initialize();
  broadcasterDiagnosticsService.initialize();

  if (location.hostname === "localhost") {
    console.log("[DI] Backoffice container initialized with keys:", DiContainer.keys());
  }
}
